import styled, { css } from 'styled-components';

const getCardBackground = ({ $isArchived }) => {
  if ($isArchived) {
    return '#FFFFFF';
  }

  return 'radial-gradient(175.36% 136.87% at 18.46% 91.07%, #FFFAF0 0%, #FFFFFF 80.17%)';
};

const getCardBorderColor = ({ $isArchived }) => {
  if ($isArchived) {
    return '#BABABA';
  }

  return '#EDC6A6';
};

export const CardContainer = styled.article`
  position: relative;
  border-radius: 8px;
  border: 0.5px solid ${getCardBorderColor};
  background: ${getCardBackground};
  padding: 12px;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
  cursor: pointer;

  ${(props) =>
    props.$isSelected &&
    css`
      border-color: #272727;
    `}

  &:hover {
    border-color: ${(props) => (props.$isArchived || props.$isSelected ? getCardBorderColor(props) : '#ED6C02')};
    box-shadow: ${(props) =>
      props.$isArchived || props.$isSelected ? 'none' : '8px 8px 16px 0 rgba(0, 0, 0, 0.08)'};
  }

  &:focus,
  &:focus-visible {
    border-color: ${(props) => (props.$isArchived ? '#BABABA' : '#272727')};
    box-shadow: none;
    outline: none;
  }

  /* Checkbox dentro del contenedor flex (TitleBlock): al hover o seleccionada ocupa espacio y el título se corre por el gap: 8px */
  &:hover .note-card-v2-checkbox,
  &[data-checkbox-visible='true'] .note-card-v2-checkbox {
    max-width: 28px;
    opacity: 1;
    pointer-events: auto;
  }

  @media (max-width: 1024px) {
    padding: 11px;
  }

  @media (min-width: 821px) and (max-width: 1024px) {
    padding: 12px;
  }

  @media (max-width: 767px) {
    padding: 10px;
  }
`;

export const CheckboxWrapper = styled.div`
  flex-shrink: 0;
  max-width: 0;
  overflow: hidden;
  opacity: 0;
  pointer-events: none;
  transition: max-width 0.12s ease-out, opacity 0.08s ease-out;

  /* El Checkbox de @ann/web-components usa transition 300ms en hover/focus.
     Acelerarla para que no quede "pegado" al ocultarse */
  & > *,
  & button {
    transition-duration: 0.08s !important;
  }
`;

export const CardMain = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

export const HeaderRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;

  @media (max-width: 820px) {
    flex-wrap: wrap;
    row-gap: 6px;
  }
`;

export const TitleBlock = styled.div`
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
`;

export const TitleText = styled.div`
  min-width: 0;
  overflow: hidden;

  > span {
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    line-height: 20px;
  }
`;

export const NoteIconWrapper = styled.div`
  flex-shrink: 0;

  /* Icono negro cuando la card está archivada */
  ${(props) =>
    props.$isArchived &&
    css`
      svg,
      [class*='icon'] {
        color: #000000 !important;
        fill: #000000 !important;
      }
    `}
`;

export const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;

  @media (max-width: 820px) {
    width: 100%;
    justify-content: space-between;
  }
`;

export const Timestamp = styled.span`
  white-space: nowrap;
  margin-top: 1px;

  @media (max-width: 767px) {
    > span {
      font-size: 12px;
      line-height: 16px;
    }
  }
`;

export const ContentContainer = styled.div`
  border: 1px solid #E9F1FF;
  border-radius: 8px;
  background: ${(props) => (props.$isArchived ? '#FFFFFF' : 'rgba(255, 255, 255, 0.5)')};
  padding: 7px 12px;
  display: flex;
  align-items: center;
  gap: 8px;

  @media (max-width: 1024px) {
    padding: 7px 10px;
  }

  @media (min-width: 821px) and (max-width: 1024px) {
    padding: 7px 12px;
  }

  @media (max-width: 767px) {
    padding: 6px 10px;
  }
`;

export const Description = styled.p`
  margin: 0;
  flex: 1;

  > span {
    display: block;
    line-height: 20px;
  }

  ${(props) =>
    !props.$isExpanded &&
    css`
      > span {
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow: hidden;
      }
    `}
`;

export const ToggleExpandButton = styled.button`
  border: none;
  background: transparent;
  padding: 0;
  margin-top: 0;
  color: ${(props) => props.theme.colors?.textSecondary || '#525252'};
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 20px;
  width: 20px;
`;

export const FooterRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  min-height: 24px;

  @media (max-width: 820px) {
    align-items: flex-start;
  }
`;

export const FooterLeft = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-height: 24px;

  @media (max-width: 820px) {
    width: 100%;
  }
`;

export const AuthorChip = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 16px;
  height: 24px;
  padding: 0 8px;
  border-radius: 8px;
  border: 1px solid ${(props) => (props.$isArchived ? '#E9F1FF' : '#f2f2f2')};
  background: ${(props) => (props.$isArchived ? 'rgba(255, 255, 255, 0.8)' : 'rgba(250, 250, 250, 0.6)')};
  color: #525252;

  @media (max-width: 820px) {
    flex-wrap: wrap;
    height: auto;
    min-height: 24px;
    padding: 4px 8px;
  }

  @media (max-width: 767px) {
    gap: 6px;
    padding: 4px 8px;
  }
`;

/* Separador entre chips — Figma: punto gris, Surface/Neutral/Emphasis. */
export const Dot = styled.span`
  width: 4px;
  height: 4px;
  min-width: 4px;
  border-radius: 999px;
  background: #bababa;
`;

export const FooterRight = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

/* Chips de metadatos — iconos 16×16px, tipografía 16px/24px Inter Regular. */
export const CounterChip = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: 24px;
  font-size: 16px;
  line-height: 24px;
  font-weight: 400;
  color: #525252;
`;

const getStatusBadgeStyles = (variant) => {
  if (variant === 'pinned') {
    return {
      border: '#B42318',
      background: '#FFF5F6',
      color: '#7A271A',
    };
  }
  if (variant === 'archived') {
    return {
      border: '#9CA3AF',
      background: '#F3F4F6',
      color: '#4B5563',
    };
  }
  return {
    border: '#737373',
    background: 'transparent',
    color: '#525252',
  };
};

export const StatusBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 24px;
  padding: 0 10px;
  border-radius: 99px;
  border: 1px solid ${(props) => getStatusBadgeStyles(props.$variant).border};
  background: ${(props) => getStatusBadgeStyles(props.$variant).background};
  color: ${(props) => getStatusBadgeStyles(props.$variant).color};

  & svg,
  & span {
    color: inherit;
  }
`;

export const MenuTriggerWrap = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;

  & .note-card-v2-menu > button > span, & .note-card-v2-menu > button > span > svg {
    height: 13px;
    width: auto;
  }

  & .note-card-v2-menu [role='menu'] {
    border-radius: 8px;
    box-shadow: 8px 8px 16px 0 rgba(0, 0, 0, 0.08);
  }

  & .note-card-v2-menu [role='menu'] > div {
    width: 260px;
    min-width: 260px;
    border: none;
    box-shadow: none;
  }
`;

export const ViewFullNoteButton = styled.button`
  border: none;
  background: transparent;
  padding: 0;
  margin: 4px 0 0;
  color: ${(props) => props.theme.colors?.primary || '#2173ff'};
  cursor: pointer;
`;

